import classnames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { t } from '../../../../../../modules/infra/i18n';
import { RoleAllowShopOrBrand } from '../../../../../auth/roleAware';
import CheckboxSwitch from '../../../../../common/checkboxSwitch';
import { BRAND_ACCESS_MESSAGE } from '../../../../../common/constants';
import CurrencyInput from '../../../../../common/currencyInput';
import { allowMaxE, inputParseFn, removeSpecialSymbols } from '../../../../../common/helpers/inputs';
import { GLYPHS } from '../../../../../common/icon';
import Input from '../../../../../common/input';
import LockableInput from '../../../../../common/LockableInput';
import LockableInputWithIcon from '../../../../../common/LockableInputWithIcon';
import LockableSelect from '../../../../../common/LockableSelect';
import LockableWrapper from '../../../../../common/LockableWrapper';
import SelectMultiple from '../../../../../common/SelectMultiple';
import Textarea from '../../../../../common/textarea';
import Tooltip from '../../../../../common/tooltip';
import UploadPhoto from '../../../../../common/uploadPhotoForm/uploadPhoto';
import { mapSelectOptions } from '../../../../helpers';
import { poVendorList } from '../../../../purchaseOrders/vendors/shapes';
import * as shapes from '../../../../shapes';
import InventoryModal from '../../../InventoryModal';

const ProductForm = ({
  categories,
  brands,
  formValues,
  taxes,
  vendors,
  isModalOpened,
  openModal,
  closeModal,
  product,
  isBrandUser,
  isEditing,
  isLimitedEditAccess,
  shopifySettings,
  trimSku,
}) => {
  const categoryOptions = categories.map(mapSelectOptions);
  const brandOptions = brands.map(mapSelectOptions);
  const taxOptions = taxes.map(mapSelectOptions);
  const vendorOptions = vendors.map(mapSelectOptions);

  const inventoryLabelSuffix = isBrandUser ? t('products:product_form.location_suffix') : '';
  const isInventoryEditableByButton = isEditing && !isBrandUser;
  const linkedShopifySKU = get(formValues, 'linkedProducts.shopify', null);

  return (
    <div className="u-max-width-710 u-margin-auto">
      <div className="c-inner-wrapper u-pt+ u-ph layout">
        <div className="layout__item">
          <div>
            <span className="c-label--section">{t('products:product_form.photo_label')}</span>
            <Tooltip className="c-info-tooltip-new--form">{t('products:product_form.photo_tooltip')}</Tooltip>
          </div>
          <UploadPhoto name="image" className="c-upload-photo u-mt-">
            {(imageUrl) => {
              if (imageUrl) {
                return (
                  <div>
                    <img className="c-upload-preview" src={imageUrl} alt="" />
                    <div className="c-btn c-btn--white c-btn--medium u-ml">
                      {t('products:product_form.change_photo')}
                    </div>
                  </div>
                );
              }
              return (
                <div className="c-btn c-btn--white c-btn--medium">+ {t('products:product_form.upload_photo')}</div>
              );
            }}
          </UploadPhoto>
        </div>
        <div className="layout__item">
          <span className="c-label-new">{t('products:product_form.name_label')}</span>
          <LockableInput
            disabled={isLimitedEditAccess}
            hint={BRAND_ACCESS_MESSAGE}
            className="c-input c-input--border c-input--v3"
            name="name"
            type="text"
            placeholder={t('products:product_form.name_placeholder')}
          />
        </div>
        <div className="layout__item">
          <span className="c-label-new">{t('products:product_form.description_label')}</span>
          <div className="c-protected-input">
            <Textarea
              disabled={isLimitedEditAccess}
              className="c-input c-input--border c-input--v3"
              name="desc"
              type="text"
              placeholder={t('products:product_form.description_placeholder')}
              parse={(val) => val || ''}
            />
            <div display-if={isLimitedEditAccess} className="c-protected-input-trigger">
              <span className="c-protected-input-popup">{BRAND_ACCESS_MESSAGE}</span>
            </div>
          </div>
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">{t('products:product_form.category_label')}</span>
          <LockableSelect
            disabled={isLimitedEditAccess}
            hint={BRAND_ACCESS_MESSAGE}
            className="c-select"
            name="productCategoryId"
            options={categoryOptions}
            parse={(val) => val || null}
            empty
          />
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">{t('products:product_form.brand_label')}</span>
          <LockableSelect
            disabled={isLimitedEditAccess}
            hint={BRAND_ACCESS_MESSAGE}
            className="c-select"
            name="productBrandId"
            parse={(val) => val || null}
            options={brandOptions}
            empty
          />
        </div>
        <RoleAllowShopOrBrand>
          <div className="layout__item u-1/2">
            <span className="c-label-new o-flexbox o-flexbox--content-align-center o-flexbox--justify-start">
              {t('products:product_form.vendor_label')}
              <Tooltip className="u-type--no-transform c-info-tooltip-new--form">
                {t('products:product_form.vendor_tooltip')}
              </Tooltip>
            </span>
            <LockableSelect
              hint={BRAND_ACCESS_MESSAGE}
              className="c-select"
              name="vendorId"
              parse={(val) => val || null}
              options={vendorOptions}
              empty
            />
          </div>
        </RoleAllowShopOrBrand>
        <div className="layout__item u-mt" display-if={taxOptions && taxOptions.length}>
          <span className="c-label--section">{t('products:product_form.tax_section_title')}</span>
          <Tooltip className="c-info-tooltip-new--form">{t('products:product_form.tax_section_tooltip')}</Tooltip>
          <SelectMultiple name="tax" options={taxOptions} />
        </div>
        <div className="layout__item u-mt">
          <span className="c-label--section">{t('products:product_form.pricing_inventory_title')}</span>
          <Tooltip className="c-info-tooltip-new--form">{t('products:product_form.pricing_inventory_tooltip')}</Tooltip>
        </div>
        <div className="layout__item u-1/3">
          <span className="c-label-new">{t('products:product_form.retail_price_label')}</span>
          <CurrencyInput
            type="number"
            className="c-input c-input--border c-input--v3"
            name="price"
            placeholder={t('products:product_form.retail_price_placeholder')}
            format={allowMaxE}
          />
        </div>
        <div className="layout__item u-1/3">
          <span className="c-label-new">{t('products:product_form.ecommerce_price_label')}</span>
          <CurrencyInput
            disabled={!shopifySettings.enablePriceSync}
            type="number"
            className="c-input c-input--border c-input--v3"
            name="ecommercePrice"
            placeholder={t('products:product_form.ecommerce_price_placeholder')}
            format={allowMaxE}
          />
        </div>
        <div className="layout__item u-1/3">
          <span className="c-label-new">{t('products:product_form.supply_price_label')}</span>
          <LockableWrapper disabled={isLimitedEditAccess} hint={BRAND_ACCESS_MESSAGE}>
            <CurrencyInput
              disabled={isLimitedEditAccess}
              type="number"
              className="c-input c-input--border c-input--v3 c-protected-input"
              name="supplyPrice"
              placeholder={t('products:product_form.supply_price_placeholder')}
              format={allowMaxE}
            />
          </LockableWrapper>
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">{t('products:product_form.total_inventory_label')}</span>
          <div className="layout layout--middle">
            <div
              className={classnames('layout__item', {
                'u-7/10 u-8/10-medium-and-up': isInventoryEditableByButton,
              })}
            >
              <Input
                type="number"
                className="c-input c-input--border c-input--v3 u-inline-block"
                name="inventory"
                parse={inputParseFn}
                onKeyDown={removeSpecialSymbols}
                placeholder={t('products:product_form.inventory_placeholder')}
                readOnly={isInventoryEditableByButton}
              />
            </div>
            <div className="layout__item u-3/10 u-2/10-medium-and-up" display-if={isInventoryEditableByButton}>
              <button type="button" className="c-btn c-btn--medium c-btn--outline u--ml-" onClick={openModal}>
                {t('products:product_form.edit_button')}
              </button>
              <InventoryModal product={product} close={closeModal} display-if={isModalOpened} />
            </div>
          </div>
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">{t('products:product_form.sku_label')}</span>
          <LockableInputWithIcon
            disabled={isLimitedEditAccess}
            hint={BRAND_ACCESS_MESSAGE}
            icon={GLYPHS.shopifyLogo}
            className="c-input c-input--border c-input--v3"
            name="sku"
            onBlur={trimSku}
            showIcon={!!linkedShopifySKU}
            type="text"
            placeholder={t('products:product_form.sku_placeholder')}
          />
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">
            {t('products:product_form.low_inventory_label')} {inventoryLabelSuffix}
          </span>
          <Input
            type="number"
            className="c-input c-input--border c-input--v3"
            name="lowInventoryThreshold"
            placeholder={`${t('products:product_form.low_inventory_placeholder')} ${inventoryLabelSuffix}`}
            parse={inputParseFn}
            onKeyDown={removeSpecialSymbols}
          />
        </div>
        <div className="layout__item u-1/2">
          <span className="c-label-new">
            {t('products:product_form.high_inventory_label')} {inventoryLabelSuffix}
          </span>
          <Input
            type="number"
            className="c-input c-input--border c-input--v3"
            name="highInventoryThreshold"
            placeholder={`${t('products:product_form.high_inventory_placeholder')} ${inventoryLabelSuffix}`}
            parse={inputParseFn}
            onKeyDown={removeSpecialSymbols}
          />
        </div>
        <div className="layout__item u-1/2 u-mv+">
          <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center">
            <CheckboxSwitch name="isRetail" id="isRetail" label={t('products:product_form.add_to_register_label')} />
            <Tooltip className="c-info-tooltip-new--form">{t('products:product_form.add_to_register_tooltip')}</Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

ProductForm.propTypes = {
  categories: shapes.brands,
  brands: shapes.brands,
  taxes: shapes.taxes,
  isModalOpened: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  product: PropTypes.oneOfType([shapes.product, PropTypes.shape({})]),
  formValues: PropTypes.oneOfType([shapes.product, PropTypes.shape({})]),
  isBrandUser: PropTypes.bool,
  isLimitedEditAccess: PropTypes.bool.isRequired,
  vendors: poVendorList,
  isEditing: PropTypes.bool.isRequired,
  shopifySettings: PropTypes.shape({
    enablePriceSync: PropTypes.bool,
  }).isRequired,
  trimSku: PropTypes.func.isRequired,
};

ProductForm.defaultProps = {
  isEditing: false,
  categories: [],
  brands: [],
  taxes: [],
  product: null,
  formValues: null,
  isBrandUser: false,
  vendors: [],
};

export default ProductForm;
